import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { otherBessInputsLibraryClient } from '@/api'
import { config } from '@/config'
import { CloneDatasetRequest } from '@/interfaces'

const BASE_ROUTE = config.links.library.otherBessInputs
const BASE_QUERY_KEY = otherBessInputsLibraryClient.QUERY_KEY

export const useOtherBessInputs = () => {
  const { showSuccess } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const getOtherBessInputsQuery = useQuery({
    queryKey: [...BASE_QUERY_KEY, { lockedOnly: false }],
    queryFn: () => otherBessInputsLibraryClient.getAll()
  })

  const addDatasetMutation = useMutation({
    mutationFn: otherBessInputsLibraryClient.addNewDataset,
    onSuccess: (datasetId: number) => {
      showSuccess('Dataset successfully added')
      navigate(`${BASE_ROUTE}/${datasetId}`)
    }
  })

  const cloneDatasetMutation = useMutation({
    mutationFn: ({
      datasetId,
      ...req
    }: CloneDatasetRequest & { datasetId: number }) =>
      otherBessInputsLibraryClient.cloneDataset(datasetId, req),
    onSuccess: (datasetId: number) => {
      showSuccess('Dataset successfully cloned')
      navigate(`${BASE_ROUTE}/${datasetId}`)
    }
  })

  const removeDatasetMutation = useMutation({
    mutationFn: otherBessInputsLibraryClient.removeDataset,
    onSuccess: async () => {
      showSuccess('Dataset successfully removed')
      await queryClient.invalidateQueries({ queryKey: BASE_QUERY_KEY })
    }
  })

  return {
    datasets: getOtherBessInputsQuery.data || [],
    isFetching: getOtherBessInputsQuery.isFetching,
    addDataset: addDatasetMutation.mutate,
    isAddDatasetInProgress: addDatasetMutation.isPending,
    removeDataset: removeDatasetMutation.mutate,
    cloneDataset: cloneDatasetMutation.mutate,
    isCloneDatasetInProgress: cloneDatasetMutation.isPending
  }
}
